import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  LinearProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import StorageIcon from '@material-ui/icons/Storage';
import PersonIcon from '@material-ui/icons/Person';
import { InfoCard, EmptyState } from '@backstage/core-components';
import type { DevxpApi } from '../api';
import type { RepositoryStats, RepositoryDetails, RepoMapping } from '../types';

const CATEGORY_COLORS: Record<string, string> = {
  'Frontend Engineering': '#e91e63',
  'Backend Systems': '#3f51b5',
  'Database & Data': '#9c27b0',
  'Infrastructure (DevOps)': '#ff9800',
  'Mobile Development': '#4caf50',
  'AI & Machine Learning': '#607d8b',
  'Testing & QA': '#00bcd4',
  'Security & Auth': '#f44336',
  'Distributed Systems': '#795548',
  'Documentation': '#9e9e9e',
};

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  searchRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  searchField: {
    flex: 1,
    maxWidth: 400,
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  repoName: {
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    marginTop: theme.spacing(1),
  },
  categoryChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.65rem',
    height: 18,
  },
  scoreBar: {
    height: 6,
    borderRadius: 3,
    marginTop: 4,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  statValue: {
    fontWeight: 700,
    fontSize: '1.5rem',
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  statBox: {
    textAlign: 'center',
    padding: theme.spacing(1.5),
  },
}));

interface RepositoriesContentProps {
  api: DevxpApi;
  onViewDeveloper?: (userId: string) => void;
}

export const RepositoriesContent = ({ api, onViewDeveloper }: RepositoriesContentProps) => {
  const [view, setView] = useState<'list' | 'details'>('list');
  const [selectedRepo, setSelectedRepo] = useState('');
  const [repoMappings, setRepoMappings] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    api.getRepoMappings().then(({ mappings }: { mappings: RepoMapping[] }) => {
      const m = new Map<string, string>();
      mappings.forEach(rm => m.set(rm.masked_name, rm.real_name));
      setRepoMappings(m);
    }).catch(() => {});
  }, [api]);

  return (
    <Box>
      {view === 'list' ? (
        <RepositoryList
          api={api}
          repoMappings={repoMappings}
          onViewRepo={repoName => {
            setSelectedRepo(repoName);
            setView('details');
          }}
        />
      ) : (
        <RepositoryDetailsView
          api={api}
          repoName={selectedRepo}
          repoMappings={repoMappings}
          onBack={() => setView('list')}
          onViewDeveloper={onViewDeveloper}
        />
      )}
    </Box>
  );
};

// ─── Repository List ──────────────────────────────────────────────────────────

interface RepositoryListProps {
  api: DevxpApi;
  repoMappings: Map<string, string>;
  onViewRepo: (repoName: string) => void;
}

const RepositoryList = ({ api, repoMappings, onViewRepo }: RepositoryListProps) => {
  const classes = useStyles();

  const [repositories, setRepositories] = useState<RepositoryStats[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const load = useCallback(
    async (pg: number, search: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getRepositories({ page: pg, pageSize, searchQuery: search });
        setRepositories(data.repositories);
        setTotalCount(data.totalCount);
        setTotalPages(data.totalPages);
      } catch (e: any) {
        setError(e.message ?? 'Failed to load repositories');
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    load(page, appliedSearch);
  }, [load, page, appliedSearch]);

  const applySearch = () => {
    setPage(1);
    setAppliedSearch(searchInput);
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <InfoCard title={`Repositories (${totalCount})`} noPadding={false}>
      <Box className={classes.searchRow}>
        <TextField
          className={classes.searchField}
          size="small"
          variant="outlined"
          label="Search repositories"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && applySearch()}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={applySearch}>
                <SearchIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <EmptyState missing="data" title="Repositories unavailable" description={error} />
      ) : repositories.length === 0 ? (
        <EmptyState missing="data" title="No repositories found" description="Try a different search." />
      ) : (
        <React.Fragment>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Repository</TableCell>
                  <TableCell align="right">Developers</TableCell>
                  <TableCell align="right">Events</TableCell>
                  <TableCell style={{ minWidth: 140 }}>Avg Proficiency</TableCell>
                  <TableCell align="right">Last Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {repositories.map(repo => (
                  <TableRow
                    key={repo.repoName}
                    className={classes.clickableRow}
                    onClick={() => onViewRepo(repo.repoName)}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <StorageIcon fontSize="small" color="action" />
                        <Typography variant="body2" className={classes.repoName}>
                          {repoMappings.get(repo.repoName) ?? repo.repoName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{repo.developerCount}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{repo.eventCount.toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell style={{ minWidth: 140 }}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {(repo.avgProficiency * 10).toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={repo.avgProficiency * 10}
                        className={classes.scoreBar}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(repo.lastActivity)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={classes.pagination}>
            <Typography variant="caption" color="textSecondary">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                Prev
              </Button>
              <Typography variant="caption">
                {page} / {totalPages}
              </Typography>
              <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </Box>
          </Box>
        </React.Fragment>
      )}
    </InfoCard>
  );
};

// ─── Repository Details ───────────────────────────────────────────────────────

interface RepositoryDetailsViewProps {
  api: DevxpApi;
  repoName: string;
  repoMappings: Map<string, string>;
  onBack: () => void;
  onViewDeveloper?: (userId: string) => void;
}

const RepositoryDetailsView = ({ api, repoName, repoMappings, onBack, onViewDeveloper }: RepositoryDetailsViewProps) => {
  const classes = useStyles();

  const [details, setDetails] = useState<RepositoryDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .getRepositoryDetails(repoName)
      .then(setDetails)
      .catch((e: any) => setError(e.message ?? 'Failed to load repository details'))
      .finally(() => setLoading(false));
  }, [api, repoName]);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <Box>
      <Box className={classes.header}>
        <Tooltip title="Back to repositories">
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <StorageIcon color="action" />
        <Typography variant="h6" className={classes.repoName}>
          {repoMappings.get(repoName) ?? repoName}
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <EmptyState missing="data" title="Details unavailable" description={error} />
      ) : details ? (
        <React.Fragment>
          {/* Summary stats */}
          <InfoCard title="Overview">
            <Box display="flex" justifyContent="space-around" flexWrap="wrap">
              <Box className={classes.statBox}>
                <Typography className={classes.statValue}>{details.developerCount}</Typography>
                <Typography className={classes.statLabel}>Developers</Typography>
              </Box>
              <Box className={classes.statBox}>
                <Typography className={classes.statValue}>{details.eventCount.toLocaleString()}</Typography>
                <Typography className={classes.statLabel}>Skill Events</Typography>
              </Box>
              <Box className={classes.statBox}>
                <Typography className={classes.statValue}>
                  {(details.avgProficiency * 10).toFixed(1)}%
                </Typography>
                <Typography className={classes.statLabel}>Avg Proficiency</Typography>
              </Box>
              <Box className={classes.statBox}>
                <Typography className={classes.statValue} style={{ fontSize: '1rem' }}>
                  {formatDate(details.lastActivity)}
                </Typography>
                <Typography className={classes.statLabel}>Last Activity</Typography>
              </Box>
            </Box>
          </InfoCard>

          {/* Category breakdown */}
          {details.categoryBreakdown.length > 0 && (
            <Box mt={2}>
              <InfoCard title="Skill Categories">
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell style={{ minWidth: 160 }}>Avg Proficiency</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.categoryBreakdown.map(cat => (
                        <TableRow key={cat.category}>
                          <TableCell>
                            <Chip
                              label={cat.category}
                              size="small"
                              className={classes.categoryChip}
                              style={{
                                backgroundColor: `${CATEGORY_COLORS[cat.category] ?? '#9e9e9e'}20`,
                                color: CATEGORY_COLORS[cat.category] ?? '#9e9e9e',
                                height: 20,
                              }}
                            />
                          </TableCell>
                          <TableCell style={{ minWidth: 160 }}>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {(cat.averageProficiency * 10).toFixed(1)}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={cat.averageProficiency * 10}
                              className={classes.scoreBar}
                              style={{ backgroundColor: `${CATEGORY_COLORS[cat.category] ?? '#9e9e9e'}30` }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Box>
          )}

          {/* Top developers */}
          {details.topDevelopers.length > 0 && (
            <Box mt={2}>
              <InfoCard title="Top Developers">
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Developer</TableCell>
                        <TableCell style={{ minWidth: 140 }}>Avg Proficiency</TableCell>
                        <TableCell align="right">Skills</TableCell>
                        {onViewDeveloper && <TableCell align="right" />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.topDevelopers.map((dev, idx) => (
                        <TableRow
                          key={dev.userId}
                          className={onViewDeveloper ? classes.clickableRow : undefined}
                          onClick={() => onViewDeveloper?.(dev.userId)}
                        >
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {idx + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {dev.userId}
                            </Typography>
                          </TableCell>
                          <TableCell style={{ minWidth: 140 }}>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {(dev.averageProficiency * 10).toFixed(1)}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={dev.averageProficiency * 10}
                              className={classes.scoreBar}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{dev.skillCount}</Typography>
                          </TableCell>
                          {onViewDeveloper && (
                            <TableCell align="right" onClick={e => e.stopPropagation()}>
                              <Tooltip title="View developer details">
                                <IconButton size="small" onClick={() => onViewDeveloper(dev.userId)}>
                                  <PersonIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Box>
          )}
        </React.Fragment>
      ) : null}
    </Box>
  );
};
